import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Loader2, ArrowLeft, ArrowRight, Building2, User, UserPlus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, apiError } from "../lib/api";
import { countryName, formatMoney } from "../i18n";
import { PasswordPolicyChecklist } from "./PasswordPolicyChecklist";

const CONSENT_VERSION = "2026-01";
const TITLE_KEYS = ["mr", "mrs", "unspecified"];
const STEP_KEYS = ["plan", "account", "type", "identity", "address", "verify", "confirm"];
const VERIFY = 5;
const CONFIRM = 6;

// Politique MDP alignée EXACTEMENT sur la politique backend par défaut (NEW_WORKSPACE_DEFAULT_POLICY).
const PW_POLICY = { min_length: 10, require_upper: true, require_lower: true, require_digit: true, require_special: true };
const pwValid = (v) =>
  (v || "").length >= 10 && /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v);

export const RegisterWizard = ({ onDone, onSwitchLogin, prefillPlan }) => {
  const { t, i18n } = useTranslation("auth");
  const lng = i18n.language;
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const [countries, setCountries] = useState([]);
  const [plans, setPlans] = useState([]);           // source de vérité = catalogue commercial (/api/plans)
  const [preview, setPreview] = useState(null);      // résolution commerciale backend
  const [previewLoading, setPreviewLoading] = useState(false);
  const [data, setData] = useState({
    plan: prefillPlan || "free",
    email: "", password: "",
    customer_type: "individual",
    title: "mr", first_name: "", last_name: "", phone: "", locale: "fr",
    company_name: "", registration_number: "", vat_number: "",
    line1: "", line2: "", city: "", postal_code: "", country_code: "",   // aucun pays par défaut (pas de NC)
    terms: false, privacy: false,
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    // Catalogue dynamique : AUCUNE liste de forfaits codée en dur ici.
    api.get("/plans").then((r) => {
      const list = (r.data.plans || []).filter((p) => p.visible !== false && p.active !== false);
      list.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setPlans(list);
      setData((d) => (list.some((p) => p.key === d.plan) ? d : { ...d, plan: list[0]?.key || "free" }));
    }).catch(() => {});
    // GeoIP = suggestion NON autoritaire. Si indisponible, aucun pays sélectionné (l'utilisateur choisit).
    api.get("/geoip/country").then((r) => {
      const c = r.data?.country;
      if (c) set("country_code", c);
    }).catch(() => {});
  }, []);

  // Résolution commerciale AVANT création : exécutée à l'entrée de l'étape Vérification
  // (et si le plan ou le pays change pendant qu'on y est).
  useEffect(() => {
    if (step !== VERIFY || !data.country_code) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    api.post("/registration/preview", { plan: data.plan, country_code: data.country_code, members: 1 })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) toast.error(apiError(e.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [step, data.plan, data.country_code]);

  const countryMeta = useMemo(
    () => countries.find((c) => c.country_code === data.country_code) || null,
    [countries, data.country_code]
  );
  const regLabel = (countryMeta?.registration_label_specific && countryMeta.registration_label) || t("wizard.identity.companyName");
  const isBusiness = data.customer_type === "business";
  const planName = (key) => plans.find((p) => p.key === key)?.name || key;
  // Item 5 : immatriculation obligatoire pour une entreprise dans un pays qui l'exige.
  const regRequired = isBusiness && !!countryMeta?.registration_required;
  const regMissing = regRequired && !data.registration_number.trim();

  const canNext = () => {
    if (step === 0) return !!data.plan;
    if (step === 1) return /\S+@\S+\.\S+/.test(data.email) && pwValid(data.password);
    if (step === 2) return !!data.customer_type;
    if (step === 3) return data.first_name.trim() && data.last_name.trim() && (!isBusiness || data.company_name.trim());
    if (step === 4) return data.line1.trim() && data.city.trim() && data.postal_code.trim() && !!data.country_code && !regMissing;
    if (step === VERIFY) return !previewLoading && !!preview && preview.eligible && !regMissing;
    if (step === CONFIRM) return data.terms && data.privacy && !regMissing;
    return true;
  };

  const buildPayload = (planOverride) => ({
    email: data.email.trim().toLowerCase(),
    password: data.password,
    plan: planOverride || data.plan,
    customer_type: data.customer_type,
    title: data.title,
    first_name: data.first_name.trim(),
    last_name: data.last_name.trim(),
    phone: data.phone.trim(),
    locale: /^(fr|en)$/.test((i18n.language || "").slice(0, 2)) ? i18n.language.slice(0, 2) : "fr",
    ...(isBusiness ? {
      company_details: {
        name: data.company_name.trim(),
        registration_number: data.registration_number.trim() || null,
        vat_number: data.vat_number.trim() || null,
      },
    } : {}),
    billing_address: {
      line1: data.line1.trim(), line2: data.line2.trim() || null,
      city: data.city.trim(), postal_code: data.postal_code.trim(),
      country_code: data.country_code,
    },
    consents: { terms: { version: CONSENT_VERSION }, privacy: { version: CONSENT_VERSION } },
  });

  const submit = async (planOverride) => {
    if (inFlight.current) return;  // anti double clic : un seul envoi en vol
    inFlight.current = true;
    setLoading(true);
    try {
      const { data: res } = await api.post("/account/register", buildPayload(planOverride));
      toast.success(t("wizard.createdSuccess"));
      onDone(res);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.code === "PAID_PLAN_UNAVAILABLE_IN_COUNTRY") {
        toast.error(t("errors.PAID_PLAN_UNAVAILABLE_IN_COUNTRY"), {
          duration: 9000,
          action: { label: t("wizard.verify.continueFree"), onClick: () => { set("plan", "free"); submit("free"); } },
        });
      } else if (detail && detail.code && i18n.exists(`auth:errors.${detail.code}`)) {
        toast.error(t(`errors.${detail.code}`));
      } else {
        toast.error(apiError(detail) || t("errors.generic"));
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const continueFree = () => { set("plan", "free"); };

  const next = () => { if (step < STEP_KEYS.length - 1) setStep(step + 1); else submit(); };
  const back = () => setStep(Math.max(0, step - 1));

  // Récapitulatif commercial (depuis la preview backend — jamais calculé localement).
  const commercialLine = () => {
    if (!preview) return "—";
    if (preview.plan === "free") return t("wizard.price.free");
    if (preview.requires_quote) return t("wizard.price.quote");
    const m = formatMoney(preview.monthly_price_minor, preview.currency, preview.currency_decimals, lng);
    return m ? t("wizard.price.perMonth", { amount: m }) : "—";
  };

  return (
    <div className="w-full max-w-lg" data-testid="register-wizard">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-6">
        {STEP_KEYS.map((k, i) => (
          <div key={k} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-emerald-500" : "bg-slate-200"}`} />
            <span className={`mt-1 block text-[10px] ${i === step ? "text-emerald-600" : "text-slate-500"}`}>{t(`wizard.steps.${k}`)}</span>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div data-testid="step-plan" className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.plan.title")}</h2>
          <div className="grid grid-cols-2 gap-3">
            {plans.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`plan-option-${p.key}`}
                onClick={() => set("plan", p.key)}
                className={`text-left rounded-xl border p-4 transition ${data.plan === p.key ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="font-semibold text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {p.requires_quote ? t("wizard.plan.quote") : (p.key === "free" ? t("wizard.plan.free") : t("wizard.plan.regional"))}
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">{t("wizard.plan.hint")}</p>
        </div>
      )}

      {step === 1 && (
        <div data-testid="step-account" className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.account.title")}</h2>
          <div><Label>{t("wizard.account.email")}</Label>
            <Input data-testid="reg-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@exemple.nc" /></div>
          <div><Label>{t("wizard.account.password")}</Label>
            <Input data-testid="reg-password" type="password" value={data.password} onChange={(e) => set("password", e.target.value)} placeholder={t("wizard.account.passwordPlaceholder")} />
            <PasswordPolicyChecklist policy={PW_POLICY} value={data.password} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="step-type" className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.type.title")}</h2>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="type-individual" onClick={() => set("customer_type", "individual")}
              className={`rounded-xl border p-5 flex flex-col items-center gap-2 transition ${!isBusiness ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
              <User className="h-6 w-6 text-emerald-600" /><span className="text-slate-900 font-medium">{t("wizard.type.individual")}</span>
            </button>
            <button type="button" data-testid="type-business" onClick={() => set("customer_type", "business")}
              className={`rounded-xl border p-5 flex flex-col items-center gap-2 transition ${isBusiness ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
              <Building2 className="h-6 w-6 text-emerald-600" /><span className="text-slate-900 font-medium">{t("wizard.type.business")}</span>
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="step-identity" className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.identity.title")}</h2>
          <div>
            <Label>{t("wizard.identity.civility")}</Label>
            <div className="flex gap-2 mt-1">
              {TITLE_KEYS.map((k) => (
                <button key={k} type="button" data-testid={`title-${k}`} onClick={() => set("title", k)}
                  className={`px-3 py-1.5 rounded-lg border text-sm ${data.title === k ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{t(`wizard.identity.${k}`)}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("wizard.identity.firstName")}</Label><Input data-testid="reg-first-name" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} /></div>
            <div><Label>{t("wizard.identity.lastName")}</Label><Input data-testid="reg-last-name" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
          </div>
          <div><Label>{t("wizard.identity.phone")}</Label><Input data-testid="reg-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          {isBusiness && (
            <div className="space-y-3 pt-2 border-t border-slate-200">
              <div><Label>{t("wizard.identity.companyName")}</Label><Input data-testid="reg-company-name" value={data.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{regLabel}</Label><Input data-testid="reg-registration-number" value={data.registration_number} onChange={(e) => set("registration_number", e.target.value)} /></div>
                <div><Label>{t("wizard.identity.vat")}</Label><Input data-testid="reg-vat-number" value={data.vat_number} onChange={(e) => set("vat_number", e.target.value)} /></div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div data-testid="step-address" className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.address.title")}</h2>
          <div><Label>{t("wizard.address.line1")}</Label><Input data-testid="reg-line1" value={data.line1} onChange={(e) => set("line1", e.target.value)} /></div>
          <div><Label>{t("wizard.address.line2")}</Label><Input data-testid="reg-line2" value={data.line2} onChange={(e) => set("line2", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("wizard.address.postal")}</Label><Input data-testid="reg-postal" value={data.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></div>
            <div><Label>{t("wizard.address.city")}</Label><Input data-testid="reg-city" value={data.city} onChange={(e) => set("city", e.target.value)} /></div>
          </div>
          <div>
            <Label>{t("wizard.address.country")}</Label>
            <select data-testid="reg-country" value={data.country_code} onChange={(e) => set("country_code", e.target.value)}
              className="mt-1 w-full rounded-md bg-white border border-slate-200 text-slate-900 px-3 py-2 text-sm">
              <option value="">{t("wizard.address.countryPlaceholder")}</option>
              {countries.map((c) => <option key={c.country_code} value={c.country_code}>{countryName(c.country_code, lng)}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-500">{t("wizard.address.countryHint")}</p>
          </div>
          {regMissing && (
            <div data-testid="reg-number-required-warning" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t("wizard.address.regRequired", { label: regLabel })}
            </div>
          )}
        </div>
      )}

      {step === VERIFY && (
        <div data-testid="step-verify" className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.verify.title")}</h2>
          {previewLoading || !preview ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : preview.eligible ? (
            <div data-testid="verify-eligible" className="rounded-xl border border-slate-200 divide-y divide-slate-200 text-sm">
              <div className="flex justify-between px-4 py-2"><span className="text-slate-500">{t("wizard.verify.plan")}</span><span className="text-slate-900">{planName(data.plan)}</span></div>
              <div className="flex justify-between px-4 py-2"><span className="text-slate-500">{t("wizard.verify.country")}</span><span className="text-slate-900">{countryName(data.country_code, lng)}</span></div>
              <div className="flex justify-between px-4 py-2"><span className="text-slate-500">{t("wizard.verify.region")}</span><span className="text-slate-900" data-testid="verify-region">{preview.commercial_region || t("wizard.verify.worldwide")}</span></div>
              <div className="flex justify-between px-4 py-2"><span className="text-slate-500">{t("wizard.verify.amount")}</span><span className="text-slate-900 font-medium" data-testid="verify-price">{commercialLine()}</span></div>
            </div>
          ) : (
            <div data-testid="verify-ineligible" className="space-y-3">
              <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <span>{t("wizard.verify.ineligible", { country: countryName(data.country_code, lng) })}</span>
              </div>
              {preview.free_available && (
                <Button type="button" data-testid="verify-continue-free" onClick={continueFree}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  {t("wizard.verify.continueFree")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {step === CONFIRM && (
        <div data-testid="step-confirm" className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">{t("wizard.confirm.title")}</h2>
          <dl className="text-sm rounded-xl border border-slate-200 divide-y divide-slate-200">
            {[
              [t("wizard.confirm.plan"), planName(data.plan)],
              [t("wizard.confirm.type"), isBusiness ? t("wizard.type.business") : t("wizard.type.individual")],
              [isBusiness ? t("wizard.confirm.companyName") : t("wizard.confirm.name"), isBusiness ? data.company_name : `${data.first_name} ${data.last_name}`],
              [t("wizard.confirm.address"), `${data.line1}, ${data.postal_code} ${data.city}`],
              [t("wizard.confirm.country"), countryName(data.country_code, lng)],
              [t("wizard.confirm.region"), preview?.commercial_region || t("wizard.verify.worldwide")],
              [t("wizard.confirm.amount"), commercialLine()],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between px-4 py-2"><dt className="text-slate-500">{k}</dt><dd className="text-slate-900 text-right">{v}</dd></div>
            ))}
          </dl>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="consent-terms" checked={data.terms} onChange={(e) => set("terms", e.target.checked)} className="mt-1" />
            <span><Trans i18nKey="wizard.confirm.terms" ns="auth" components={[<Link to="/cgu" className="text-emerald-600 underline" target="_blank" />]} /></span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="consent-privacy" checked={data.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-1" />
            <span><Trans i18nKey="wizard.confirm.privacy" ns="auth" components={[<Link to="/confidentialite" className="text-emerald-600 underline" target="_blank" />]} /></span>
          </label>
          <p className="text-xs text-slate-500">{t("wizard.confirm.recalcNote")}</p>
        </div>
      )}

      {/* Nav */}
      <div className="flex items-center justify-between mt-7">
        {step > 0 ? (
          <Button type="button" variant="ghost" data-testid="wizard-back" onClick={back} className="text-slate-600">
            <ArrowLeft className="h-4 w-4 mr-1" /> {t("common:common.back")}
          </Button>
        ) : <span />}
        <Button type="button" data-testid="wizard-next" disabled={!canNext() || loading} onClick={next}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : step === STEP_KEYS.length - 1 ? (<><UserPlus className="h-4 w-4 mr-1" /> {t("wizard.submit")}</>) : (<>{t("common:common.continue")} <ArrowRight className="h-4 w-4 ml-1" /></>)}
        </Button>
      </div>
      <p className="mt-5 text-center text-sm text-slate-500">
        {t("login.haveAccount")}{" "}
        <button type="button" data-testid="wizard-switch-login" onClick={onSwitchLogin} className="text-emerald-600 font-medium">{t("login.signin")}</button>
      </p>
    </div>
  );
};
